import React, { useEffect } from "react";
import CustomFieldValue from "./models/CustomFieldValue";
import DonorDetails from "./store/actions/DonorDetails";
import * as Yup from "yup";
import Axios from "axios";
import { apiBaseUrl } from "./AppSettings";
import { Formik } from "formik";    
import { Button, Form, Row, Col } from "react-bootstrap";
import { updateDonorDetails } from "./store/actions/DonorDetailsActions";
import { setEventGroupName } from "./store/actions/UIStateActions";
import { updateSevaDetailsForEvent } from "./store/actions/DonationDetailsActions";
import Event from "./models/Event";
import { connect } from "react-redux";
import getCentersForEventGroup from "./EventGroupAllowedCenterMap";
import Centre from "./models/Centre";
import { Redirect } from "react-router-dom";

interface DispatchProps {
    updateDonorDetails: (donorDetails: DonorDetails) => void;
    updateEventGroupName: (eventGroupName: string) => void;
    updateSevaDetailsForEvent: (
        sevaCount: number,
        customFields: Array<CustomFieldValue>,
        event: Event) => void;
}

interface StateProps {
    donor: any;
}

type ComponentProps = DispatchProps & StateProps;

interface SatsangExamRegistrationProps {
    firstName: string;
    middleName: string;
    lastName: string;
    gender: string;
    age: number;
    centre: string;
    email: string;
    mobileNumber: string;
    streetAddress: string;
    suburb: string;
    zip: string;
    examLanguage: string;
    event: string;
}

const initialValues: SatsangExamRegistrationProps = {
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    age: 0,
    email: "",
    mobileNumber: "",
    streetAddress: "",
    suburb: "",
    zip: "",
    examLanguage: "",
    centre: "", // Default to Australia
    event: "shishu-satsang-vihar-1" // Default to Shishu Satsang Vihar 1
}

export function BalSatsangExamRegistration(props: ComponentProps) {
    const [allDetailsEntered, setAllDetailsEntered] = React.useState<boolean>(false);
    const [events, setEvents] = React.useState<Array<Event>>([]);

    const formSchema = Yup.object().shape({
        firstName: Yup.string().required().max(50),
        lastName: Yup.string().required().max(50),
        middleName: Yup.string().required().max(50),
        gender: Yup.string().required().matches(/^(male|female)$/i, "Please select a valid gender"),
        age: Yup.number().required().min(9).max(13).typeError("Age must be a number between 9 and 13"),
        mobileNumber: Yup.string().matches(/^(04[0-9]{8}|02[0-9]{7,9})$/gm, "Please enter Australian mobile number (04xxxxxxxx) or New Zealand mobile number (02xxxxxxx)").required(),
        email: Yup.string().email().required(),
        streetAddress: Yup.string().required(),
        suburb: Yup.string().required(),
        zip: Yup.number().required(),
        centre: Yup.string().required(),
        examLanguage: Yup.string().required().matches(/^(english|gujarati|hindi)$/i, "Please select a valid exam language"),
        event: Yup.string().required().oneOf(["shishu-satsang-vihar-1", "shishu-satsang-vihar-2", "satsang-vihar-1", "satsang-vihar-2", "satsang-vihar-3"], "Please select a valid exam")
    });

    const renderCentreSelect = (): Array<JSX.Element> => {
        const allowedCenters = getCentersForEventGroup("bal-satsang-exam-2025");
        const optionsForCentres = new Array<JSX.Element>();

        allowedCenters.forEach(eachCenter => {
            const keyNumeric: number = eachCenter;

            if (isNaN(keyNumeric))
                return;

            optionsForCentres.push(<option key={`Centre-${keyNumeric}`} value={keyNumeric}>{Centre[keyNumeric]}</option>)
        });

        return optionsForCentres;
    }

    useEffect(() => {
        props.updateEventGroupName("bal-satsang-exam-2025");
        loadEvents();
    }, []);

    const loadEvents = async () => {
        const response = await Axios.get<Array<Event>>(`${apiBaseUrl}/api/events?eventGroupName=bal-satsang-exam-2025`);
        const events = response.data;
        setEvents(events);
    }

    const inferEvent = (eventName: string, language: string): Event | undefined => {
        const event = events.find(event => event.name === `${eventName}-${language}`);
        return event;
    }

    const onSubmit = (values: SatsangExamRegistrationProps) => {

        const selectedEvent = inferEvent(values.event, values.examLanguage);
        if (!selectedEvent) {
            alert("Selected event is not available. Please try again later.");
            return;
        }

        props.updateDonorDetails({
            firstName: `${values.firstName} ${values.middleName}`,
            centre: Number.parseInt(values.centre),
            email: values.email,
            lastName: values.lastName,
            mobile: values.mobileNumber,
            address: values.streetAddress,
            zip: Number.parseInt(values.zip, 10),
            suburb: values.suburb,
            balsatsangExamAdditionalDetails: {
                age: values.age,
                gender: values.gender
            },
            customAttributes: {}
        });
        props.updateSevaDetailsForEvent(1, [], selectedEvent);
        setAllDetailsEntered(true);
    }

    const renderForm = () => (<Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={formSchema}
    >
        {formikProps => (
            <Form onSubmit={formikProps.handleSubmit}>
                {/* console.log(JSON.stringify(formikProps.errors)) */}
                <Row>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formFirstName">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter first name"
                                onChange={formikProps.handleChange}
                                value={formikProps.values.firstName}
                                name="firstName" />
                            <Form.Text className="text-muted">
                                Please enter first name.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.firstName &&
                                formikProps.errors.firstName ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.firstName}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formMiddleName">
                            <Form.Label>Middle Name</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter middle name"
                                onChange={formikProps.handleChange}
                                value={formikProps.values.middleName}
                                name="middleName" />
                            <Form.Text className="text-muted">
                                Please enter middle name.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.middleName &&
                                formikProps.errors.middleName ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.middleName}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formLastName">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter last name"
                                onChange={formikProps.handleChange}
                                value={formikProps.values.lastName}
                                name="lastName" />
                            <Form.Text className="text-muted">
                                Please enter last name.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.lastName &&
                                formikProps.errors.lastName ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.lastName}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formGender">
                            <Form.Label>Gender</Form.Label>
                            <Form.Control as="select"
                                name="gender"
                                value={formikProps.values.gender}
                                onChange={formikProps.handleChange}>
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </Form.Control>
                            <Form.Text className="text-muted">
                                Please select your gender.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.gender &&
                                formikProps.errors.gender ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.gender}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formAge">
                            <Form.Label>Age</Form.Label>
                            <Form.Control type="number"
                                placeholder="Enter age"
                                name="age"
                                value={formikProps.values.age}
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter your age (9-13).
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.age &&
                                formikProps.errors.age ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.age}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={4} sm={12}>
                        <Form.Label>Center</Form.Label>
                        <Form.Control as="select"
                            name="centre"
                            onChange={formikProps.handleChange} >
                            <option></option>
                            {renderCentreSelect()}
                        </Form.Control>
                        <Form.Text className="text-muted">
                            Please select your centre.
                        </Form.Text>
                        {formikProps.touched &&
                            formikProps.touched.centre &&
                            formikProps.errors.centre ?
                            (<div className="fade alert alert-danger show">{formikProps.errors.centre}</div>) : (<React.Fragment />)}
                    </Col>
                </Row>

                <Row>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formExamLanguage">
                            <Form.Label>Exam Language</Form.Label>
                            <Form.Control as="select"
                                name="examLanguage"
                                value={formikProps.values.examLanguage}
                                onChange={formikProps.handleChange}>
                                <option value="">Select Exam Language</option>
                                <option value="English">English</option>
                                <option value="Gujarati">Gujarati</option>
                            </Form.Control>
                            <Form.Text className="text-muted">
                                Please select the language for the exam.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.examLanguage &&
                                formikProps.errors.examLanguage ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.examLanguage}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={4} sm={12}>
                        <Form.Group controlId="formEvent">
                            <Form.Label>Exam</Form.Label>
                            <Form.Control as="select"
                                name="event"
                                onChange={formikProps.handleChange}>
                                <option value="shishu-satsang-vihar-1">Shishu Satsang Vihar 1 (Ages 9-10)</option>
                                <option value="shishu-satsang-vihar-2">Shishu Satsang Vihar 2 (Ages 9-10)</option>
                                <option value="satsang-vihar-1">Satsang Vihar 1 (Ages 11-13)</option>
                                <option value="satsang-vihar-2">Satsang Vihar 2 (Ages 11-13)</option>
                                <option value="satsang-vihar-3">Satsang Vihar 3 (Ages 11-13)</option>
                            </Form.Control>
                            <Form.Text className="text-muted">
                                Please select the exam you want to register for.
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.event &&
                                formikProps.errors.event ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.event}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} sm={12}>
                        <Form.Group controlId="formEmail">
                            <Form.Label>Email address</Form.Label>
                            <Form.Control type="email"
                                placeholder="Enter email"
                                name="email"
                                value={formikProps.values.email}
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter your email id
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.email &&
                                formikProps.errors.email ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.email}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={6} sm={12}>
                        <Form.Group controlId="formMobile">
                            <Form.Label>Mobile number</Form.Label>
                            <Form.Control type="text"
                                placeholder="04XXXXXXXX (AU) or 02XXXXXXX (NZ)"
                                pattern="^(04[0-9]{8}|02[0-9]{7,9})$"
                                name="mobileNumber"
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter your mobile number (AU: 04xxxxxxxx or NZ: 02xxxxxxx).
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.mobileNumber &&
                                formikProps.errors.mobileNumber ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.mobileNumber}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} sm={12}>
                        <Form.Group controlId="formAddress">
                            <Form.Label>Street Address</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter Address"
                                name="streetAddress"
                                value={formikProps.values.streetAddress}
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter your address
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.streetAddress &&
                                formikProps.errors.streetAddress ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.streetAddress}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={3} sm={12}>
                        <Form.Group controlId="formSubub">
                            <Form.Label>Suburb</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter Suburb"
                                name="suburb"
                                value={formikProps.values.suburb}
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter your suburb
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.suburb &&
                                formikProps.errors.suburb ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.suburb}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                    <Col md={3} sm={12}>
                        <Form.Group controlId="formZip">
                            <Form.Label>Zipcode</Form.Label>
                            <Form.Control type="text"
                                placeholder="Enter ZipCode"
                                name="zip"
                                value={formikProps.values.zip}
                                onChange={formikProps.handleChange} />
                            <Form.Text className="text-muted">
                                Please enter ZipCode
                            </Form.Text>
                            {formikProps.touched &&
                                formikProps.touched.zip &&
                                formikProps.errors.zip ?
                                (<div className="fade alert alert-danger show">{formikProps.errors.zip}</div>) : (<React.Fragment />)}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} sm={12}>
                        <Button variant="primary" type="submit" size="lg">
                            NEXT
                        </Button>
                    </Col>
                </Row>

            </Form>
        )}

    </Formik>);

    const renderRedirect = () => (<Redirect to="/confirm" />);

    return (<div>

        {allDetailsEntered ? renderRedirect() : renderForm()}
    </div>)

}

const mapStateToProps = (state: any): StateProps => {
    return {
        donor: state.donor,
    };
}


const mapDispatchToProps = (dispatch: any): DispatchProps => {
    return {
        updateDonorDetails: (donorDetails: DonorDetails) => dispatch(updateDonorDetails(donorDetails)),
        updateEventGroupName: (eventGroupName: string) => dispatch(setEventGroupName(eventGroupName)),
        updateSevaDetailsForEvent: (
            sevaCount: number,
            customFieldValues: CustomFieldValue[],
            event: Event) =>
            dispatch(updateSevaDetailsForEvent(sevaCount, customFieldValues, event))
    }
}

export default connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(BalSatsangExamRegistration);